import type { FC, ReactNode } from 'react'
import type { UrlObject } from 'url'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { formatNumber } from '@/lib/format'

export interface SegmentedOption {
    key: string
    label: string
    count?: number
}

interface SegmentedLinksProps {
    label: string
    options: SegmentedOption[]
    active: string
    hrefFor: (key: string) => UrlObject
    className?: string
}

export const SegmentedLinks: FC<SegmentedLinksProps> = ({ label, options, active, hrefFor, className }): ReactNode => {
    return (
        <nav aria-label={label} className={cn('no-scrollbar max-w-full overflow-x-auto', className)}>
            <ul className='inline-flex min-w-max items-center gap-0.5 rounded-lg bg-secondary p-1'>
                {options.map(option => {
                    const selected = option.key === active

                    return (
                        <li key={option.key}>
                            <Link
                                href={hrefFor(option.key)}
                                scroll={false}
                                aria-current={selected ? 'true' : undefined}
                                className={cn(
                                    'flex h-8 items-center gap-1.5 rounded-md px-3 text-sm font-medium whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                    selected
                                        ? 'bg-card text-foreground shadow-[0_1px_2px_rgba(16,24,40,0.08)]'
                                        : 'text-muted-foreground hover:text-foreground'
                                )}
                            >
                                {option.label}

                                {option.count !== undefined && (
                                    <span
                                        className={cn(
                                            'rounded-full px-1.5 text-xs font-semibold tabular-nums',
                                            selected ? 'bg-primary/10 text-primary' : 'bg-card/70 text-muted-foreground'
                                        )}
                                    >
                                        {formatNumber(option.count)}
                                    </span>
                                )}
                            </Link>
                        </li>
                    )
                })}
            </ul>
        </nav>
    )
}
